import { useCallback, useState } from "react";
import { RefreshCw, Stethoscope } from "lucide-react";
import {
  diagnoseConsole,
  type ConsoleDiagnoseReport,
} from "@/api/endpoints/vms";

/**
 * In-overlay diagnostics button for the VNC console.
 *
 * The backend's ``GET /api/v1/vms/{id}/console/diagnose`` walks every
 * step of the console setup without opening a WebSocket (VM lookup,
 * node URL, vncproxy ticket, TCP probe to the Proxmox API port, auth
 * mode). Keeping this UI isolated from ConsolePage makes it possible
 * to test the rendering of each ok/fail/skipped row in jsdom without
 * mocking the noVNC bundle or the WebSocket shape of the parent page.
 *
 * Kept in the ``console`` component folder rather than co-located
 * alongside ConsolePage because the page itself is a full-screen
 * standalone view — grouping reusable pieces by feature is easier to
 * discover.
 */
export function ConsoleDiagnoseButton({ vmId }: { vmId: number }) {
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "running" }
    | { kind: "ok"; report: ConsoleDiagnoseReport }
    | { kind: "err"; message: string }
  >({ kind: "idle" });

  const run = useCallback(async () => {
    setState({ kind: "running" });
    try {
      const report = await diagnoseConsole(vmId);
      setState({ kind: "ok", report });
    } catch (err) {
      setState({
        kind: "err",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [vmId]);

  if (state.kind === "ok") {
    return <ConsoleDiagnoseReportView report={state.report} onRerun={run} />;
  }
  return (
    <>
      <button
        onClick={run}
        disabled={state.kind === "running"}
        className="rounded border border-amber-600/40 bg-amber-700/20 px-3 py-1.5 text-sm text-amber-200 hover:bg-amber-700/30 disabled:opacity-50"
        title="Walk the console setup end-to-end (no WebSocket opened)"
      >
        <Stethoscope className="mr-1 inline h-3 w-3" />
        {state.kind === "running" ? "Running…" : "Run diagnostics"}
      </button>
      {state.kind === "err" && (
        <p className="mt-2 w-full text-xs text-red-400">
          Diagnostics failed: {state.message}
        </p>
      )}
    </>
  );
}

export function ConsoleDiagnoseReportView({
  report,
  onRerun,
}: {
  report: ConsoleDiagnoseReport;
  onRerun: () => void;
}) {
  const vmOk = report.vm !== null;
  const nodeOk = !!report.node?.base_url;
  const vncproxy = report.vncproxy;
  const wsReach = report.proxmox_ws_reachable;

  // The diagnose endpoint short-circuits on the first hard failure, so
  // downstream checks may legitimately be ``null``. Render those as
  // "not reached" rather than pretending they passed.
  const vncproxyState: CheckState = !vncproxy
    ? "skipped"
    : vncproxy.ok
      ? "ok"
      : "fail";
  const wsReachState: CheckState = !wsReach
    ? "skipped"
    : wsReach.ok
      ? "ok"
      : "fail";

  return (
    <div className="mt-4 w-full rounded border border-gray-700 bg-gray-950/60 p-3 text-xs">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-300">
          Console diagnostics
        </span>
        <button
          onClick={onRerun}
          className="text-[10px] text-gray-400 hover:text-gray-200"
          title="Re-run"
          aria-label="Re-run diagnostics"
        >
          <RefreshCw className="h-3 w-3" />
        </button>
      </div>
      <ul className="space-y-1">
        <CheckRow
          state={vmOk ? "ok" : "fail"}
          label="VM lookup"
          detail={
            report.vm
              ? `#${report.vm.vmid} ${report.vm.name} on ${report.vm.node}`
              : "not found"
          }
        />
        <CheckRow
          state={nodeOk ? "ok" : "fail"}
          label="Proxmox node URL"
          detail={
            report.node?.base_url ?? `node ${report.node?.name ?? "?"} unmapped`
          }
        />
        <CheckRow
          state={vncproxyState}
          label="vncproxy ticket"
          detail={
            vncproxy?.ok
              ? `port ${vncproxy.port}, ticket ${vncproxy.has_ticket ? "present" : "missing"}`
              : vncproxy && !vncproxy.ok
                ? vncproxy.error
                : "not reached"
          }
        />
        <CheckRow
          state={wsReachState}
          label="Proxmox :8006 reachable"
          detail={
            wsReach?.ok
              ? `${wsReach.host}:${wsReach.port} OK`
              : wsReach && !wsReach.ok
                ? `${wsReach.host}:${wsReach.port} — ${wsReach.error}`
                : "not reached"
          }
        />
      </ul>
      {report.warnings.length > 0 && (
        <div className="mt-2 space-y-1">
          {report.warnings.map((w, i) => (
            <div
              key={i}
              className="rounded border border-amber-700/40 bg-amber-600/10 px-2 py-1 text-[11px] text-amber-200"
            >
              {w}
            </div>
          ))}
        </div>
      )}
      {report.proxmox_auth_mode && (
        <p className="mt-2 text-[10px] leading-snug text-gray-500">
          Auth mode: <span className="font-mono">{report.proxmox_auth_mode.current}</span>
          {" — "}
          {report.proxmox_auth_mode.note}
        </p>
      )}
    </div>
  );
}

type CheckState = "ok" | "fail" | "skipped";

function CheckRow({
  state,
  label,
  detail,
}: {
  state: CheckState;
  label: string;
  detail: string;
}) {
  const mark =
    state === "ok" ? (
      <span className="font-mono text-emerald-400">✓</span>
    ) : state === "fail" ? (
      <span className="font-mono text-red-400">✗</span>
    ) : (
      <span className="font-mono text-gray-500">·</span>
    );
  const detailClass =
    state === "fail"
      ? "text-red-300"
      : state === "skipped"
        ? "text-gray-500"
        : "text-gray-300";
  return (
    <li className="flex items-start gap-2">
      <span className="mt-0.5">{mark}</span>
      <span className="w-40 shrink-0 text-gray-400">{label}</span>
      <span className={`flex-1 break-all ${detailClass}`}>{detail}</span>
    </li>
  );
}
